import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/data/services";
import { cn } from "@/lib/utils";

export default function Services() {
  return (
    <>
      <Helmet>
        <title>Services | Mangawhai Heat Pumps, AC, Refrigeration & Ventilation | Cool Wave</title>
        <meta
          name="description"
          content="Heat pumps, air conditioning, refrigeration and home ventilation services across Mangawhai, Northland and Auckland."
        />
      </Helmet>

      <section className="relative overflow-hidden bg-background">
        <div className="relative min-h-[420px] md:min-h-[480px] lg:min-h-[520px]">
          <img
            src="/images/hero-services.jpg"
            alt="Modern wall-mounted split-system heat pump in a stylish home interior"
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/55 to-ink/0" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-transparent to-transparent" />
          <div className="container relative flex min-h-[420px] items-center md:min-h-[480px] lg:min-h-[520px]">
            <div className="max-w-2xl py-12 md:py-16">
              <span className="mb-5 inline-block rounded-full bg-white/95 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700 shadow-sm backdrop-blur">
                Our Services
              </span>
              <h1 className="mb-5 text-4xl font-bold leading-[1.05] tracking-tight text-white [text-shadow:_0_2px_16px_rgb(0_0_0_/_45%)] md:text-5xl lg:text-6xl">
                Heating, cooling & ventilation done properly.
              </h1>
              <p className="max-w-xl text-lg text-white/95 [text-shadow:_0_1px_8px_rgb(0_0_0_/_55%)]">
                Designed and installed properly the first time, then backed up with reliable servicing for the long haul.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-[linear-gradient(to_bottom,#EAF6FE_0%,#EAF6FE_97%,#FFFFFF_100%)]">
      <div className="border-b border-border/60 py-8 md:py-10">
        <div className="container">
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {SERVICES.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="group inline-flex items-center gap-2.5 rounded-full border-2 border-brand-700 bg-white px-5 py-3 text-sm font-semibold tracking-tight text-brand-800 shadow-sm ring-2 ring-brand-700/10 transition hover:-translate-y-0.5 hover:bg-brand-700 hover:text-white hover:shadow-md md:px-6 md:py-3.5 md:text-base"
              >
                <s.icon className="h-5 w-5 transition-colors md:h-[1.125rem] md:w-[1.125rem]" />
                <span>{s.title}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="container divide-y divide-border/60">
        {SERVICES.map((s, idx) => (
          <section key={s.id} id={s.id} className={cn("scroll-mt-28 py-14 md:py-20 md:scroll-mt-32", idx === 0 && "pt-12")}>
            <div className="grid gap-10 md:grid-cols-[1fr,1fr] md:gap-16">
              <div>
                <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand">
                  <s.icon className="h-6 w-6" />
                </div>
                <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl">{s.title}</h2>
                <p className="mb-6 text-body">{s.long}</p>
                <Link
                  to="/free-quote"
                  className="inline-flex h-11 items-center justify-center rounded-md bg-brand px-5 text-sm font-semibold text-white hover:bg-brand-600"
                >
                  Get a Quote
                </Link>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink">Residential</h3>
                  <ul className="space-y-2 text-sm text-body">
                    {s.residential.map((sub) => (
                      <li key={sub} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-brand" /> {sub}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink">Commercial</h3>
                  <ul className="space-y-2 text-sm text-body">
                    {s.commercial.map((sub) => (
                      <li key={sub} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-brand" /> {sub}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>
      </div>
    </>
  );
}
